import { FC, SVGProps, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { ReactComponent as FacilityFacilityIcon } from 'src/assets/svg/comparison-type-facility-facility.svg'
import { ReactComponent as FacilityFacilityPlatformIcon } from 'src/assets/svg/comparison-type-facility-facility-platform.svg'
import { ReactComponent as FacilityPlatformsIcon } from 'src/assets/svg/comparison-type-facility-platforms.svg'
import { ReactComponent as PlatformLocationsIcon } from 'src/assets/svg/comparison-type-platform-locations.svg'
import { ReactComponent as PlatformsBreakdownIcon } from 'src/assets/svg/comparison-type-platforms-breakdown.svg'
import { cn } from 'src/utils/cn'
import styles from './newReviewComparisonModal.module.scss'

// What the report compares. The type decides what the last step asks for, which
// is why it is picked before the facilities rather than alongside them.
export type ReviewComparisonType =
  | 'facilityFacility'
  | 'facilityFacilityPlatform'
  | 'facilityPlatforms'
  | 'platformLocations'
  | 'platformsFacilityBreakdown'

interface NewReviewComparisonModalProps {
  // The facilities this campaign has, in the order the page lists them.
  facilities: string[]
  isOpen: boolean
  onClose: () => void
  // What the modal was for: the facilities picked, the window, and the label
  // the window was picked under. The caller saves it — the modal knows what
  // was chosen, not what to do with it.
  onGenerate: (
    facilities: string[],
    window: { from: string; to: string },
    quickCompare: string | null
  ) => void
}

// A pair. The report puts one facility against one other in every section, so
// offering more would produce facilities it has nowhere to draw — the mistake
// Social Listening's picker made, where four could be chosen and two were
// silently dropped from most of the report.
const MIN_FACILITIES = 2
const MAX_FACILITIES = 2

const COPY = {
  close: 'Close',
  // Says why the option cannot be picked, on the option itself.
  comingSoon: 'Coming Soon',
  facilitiesHint:
    'The comparison will be made using an aggregated assessment of all platforms.',
  facilitiesLimit:
    'Limit reached: you have selected 2 facilities. Unselect one to choose another.',
  facilitiesSelected: (count: number) =>
    `Selected ${count} of ${MAX_FACILITIES}`,
  facilitiesTitle: 'Selecting facilities',
  generate: 'Generate Report',
  locationsStepSubtitle: 'Choosing specific objects or platforms.',
  locationsStepTitle: 'Locations / Platforms',
  locationsSubtitle:
    'Depending on the type chosen, highlight specific facilities and/or platforms.',
  locationsTitle: 'Facilities and platforms',
  min: 'Min. 2',
  periodStepSubtitle: 'Which period of time do you want to compare data from?',
  periodStepTitle: 'Time period',
  periodSubtitle: 'Select a quick period or set custom dates.',
  periodTitle: 'Which period are you interested in?',
  platformsAll: 'All platforms (automatically)',
  platformsNote:
    'In this type of comparison, platforms are not broken down individually — the data is aggregated.',
  platformsTitle: 'Platforms',
  steps: '3 steps',
  title: 'New Comparison',
  typeStepSubtitle: 'In what context are we making the comparison?',
  typeStepTitle: 'Comparison type',
  typeSubtitle:
    'The type determines which objects or platforms you will compare in the next step.',
  typeTitle: 'What type of comparison are you looking for?',
}

const RANGE_FORMAT = 'MMM D, YYYY'

interface QuickPeriod {
  end: () => moment.Moment
  label: string
  start: () => moment.Moment
}

const QUICK_PERIODS: QuickPeriod[] = [
  {
    end: () => moment().subtract(1, 'day'),
    label: 'Yesterday',
    start: () => moment().subtract(1, 'day'),
  },
  {
    end: () => moment(),
    label: 'Last 7 days',
    start: () => moment().subtract(6, 'days'),
  },
  {
    end: () => moment(),
    label: 'Last 30 days',
    start: () => moment().subtract(29, 'days'),
  },
  {
    end: () => moment(),
    label: 'This Month',
    start: () => moment().startOf('month'),
  },
]

// The state the calendar is already in when the step opens, before a shortcut
// narrows it — so it is the default selection rather than a fifth period.
const CUSTOM_RANGE = 'Custom Range'

// Calendar days, not instants: the API reads these as UTC days, and an ISO
// timestamp from a picker east of UTC floors to the previous day.
const API_DATE_FORMAT = 'YYYY-MM-DD'

// The five comparisons this report can be. Each names what it compares and, in
// its own colour, the platform treatment that goes with it — that pair is what
// tells the first two options apart.
const TYPES: {
  // Not built yet. The option is still listed — it says what is coming — but it
  // cannot be picked, so nobody chooses a type the report cannot produce.
  comingSoon?: boolean
  description: string
  icon: FC<SVGProps<SVGSVGElement>>
  qualifier?: string
  title: string
  value: ReviewComparisonType
}[] = [
  {
    description:
      'Compare two or more facilities — ratings are aggregated from all platforms together.',
    icon: FacilityFacilityIcon,
    qualifier: 'All platforms in total',
    title: 'Facility - Facility',
    value: 'facilityFacility',
  },
  {
    comingSoon: true,
    description:
      'Compare facilities with data from only one, specifically selected platform.',
    icon: FacilityFacilityPlatformIcon,
    qualifier: 'With a specific platform',
    title: 'Facility - Facility',
    value: 'facilityFacilityPlatform',
  },
  {
    comingSoon: true,
    description:
      'A common image of one facility on all platforms + a detailed cut on one, specific platform.',
    icon: FacilityPlatformsIcon,
    title: 'Facility with all & Specific platforms',
    value: 'facilityPlatforms',
  },
  {
    comingSoon: true,
    description:
      'A comparison of platforms, where each platform is broken down by featured facilities.',
    icon: PlatformsBreakdownIcon,
    title: 'Platforms with Facility breakdown',
    value: 'platformsFacilityBreakdown',
  },
  {
    comingSoon: true,
    description:
      'Comparison of several facilities/locations within a specific platform.',
    icon: PlatformLocationsIcon,
    qualifier: 'All platforms in total',
    title: 'One platform - Many locations comparison',
    value: 'platformLocations',
  },
]

export const NewReviewComparisonModal: FC<NewReviewComparisonModalProps> = ({
  facilities,
  isOpen,
  onClose,
  onGenerate,
}) => {
  // Whose reviews the calendar's dots count. Review data lives in its own
  // module, keyed by the campaign this page is for, so the source has to name
  // both — 'social-listening' dots would mark days this feature knows nothing
  // about.
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [start, setStart] = useState<moment.Moment>(() =>
    moment().subtract(29, 'days')
  )
  const [end, setEnd] = useState<moment.Moment>(() => moment())
  const [activePeriod, setActivePeriod] = useState(CUSTOM_RANGE)
  // Bumped only when a quick period is chosen. CustomCalendar reads
  // initialStart/initialEnd once, at mount, so remounting is what makes it
  // adopt the new range. Deliberately NOT keyed on the range itself: a remount
  // between the two clicks of a manual range would throw the first one away.
  const [calendarKey, setCalendarKey] = useState(0)
  const [type, setType] = useState<ReviewComparisonType>('facilityFacility')
  const [selected, setSelected] = useState<string[]>([])

  // The page behind an open modal must not scroll: the overlay covers it, so
  // scrolling it moves something the reader cannot see. Called before the early
  // return, since hooks cannot be skipped.
  useDisableScroll(isOpen)

  if (!isOpen) {
    return null
  }

  const toggleFacility = (facility: string) => {
    setSelected((current) => {
      if (current.includes(facility)) {
        return current.filter((each) => each !== facility)
      }

      // At the limit the pick is refused rather than silently swapping one of
      // the two out — which of them would it drop?
      return current.length >= MAX_FACILITIES ? current : [...current, facility]
    })
  }

  const handleQuickPeriod = (period: QuickPeriod) => {
    setActivePeriod(period.label)
    setStart(period.start())
    setEnd(period.end())
    setCalendarKey((current) => current + 1)
  }

  const rangeLabel = `${start.format(RANGE_FORMAT)} – ${end.format(
    RANGE_FORMAT
  )}`

  const paneHeader = (title: string, subtitle: string) => (
    <div className={styles.paneHeader}>
      <div>
        <h3 className={styles.paneTitle}>{title}</h3>
        <p className={styles.paneSubtitle}>{subtitle}</p>
      </div>

      <button
        aria-label={COPY.close}
        className={styles.closeButton}
        onClick={onClose}
        type="button"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  )

  const periodStep = (
    <>
      {paneHeader(COPY.periodTitle, COPY.periodSubtitle)}

      <div className={styles.quickPeriods}>
        {[...QUICK_PERIODS.map((period) => period.label), CUSTOM_RANGE].map(
          (label) => (
            <button
              className={cn(
                styles.quickPeriod,
                activePeriod === label && styles.quickPeriodActive
              )}
              key={label}
              onClick={() => {
                const period = QUICK_PERIODS.find(
                  (option) => option.label === label
                )

                if (period) {
                  handleQuickPeriod(period)
                  return
                }

                setActivePeriod(CUSTOM_RANGE)
              }}
              type="button"
            >
              {label}
            </button>
          )
        )}
      </div>

      <div className={styles.calendarBox}>
        {/* The modal owns the footer — Next is this step's Apply — so the
            calendar reports each pick instead of waiting for its own button. */}
        <CustomCalendar
          analysisId={campaignId}
          className={styles.calendar}
          initialEnd={end}
          initialStart={start}
          key={calendarKey}
          monthsToShow={2}
          navLabel="Navigation for the selected period"
          onApply={() => undefined}
          onClose={() => undefined}
          onSelect={(nextStart, nextEnd) => {
            setActivePeriod(CUSTOM_RANGE)
            setStart(nextStart)
            setEnd(nextEnd ?? nextStart)
          }}
          showFooter={false}
          // Dots under the days that hold reviews, so a window is picked
          // knowing where the data is rather than by trial and error.
          source="review-analysis"
        />
      </div>

      <div className={styles.footer}>
        <span className={styles.footerLabel}>{rangeLabel}</span>

        <button className={styles.backButton} onClick={onClose} type="button">
          Back
        </button>

        <button
          className={styles.primaryButton}
          onClick={() => setStep(2)}
          type="button"
        >
          Next
        </button>
      </div>
    </>
  )

  const typeStep = (
    <>
      {paneHeader(COPY.typeTitle, COPY.typeSubtitle)}

      <div className={styles.typeList}>
        {TYPES.map((option) => {
          const Icon = option.icon

          return (
            <button
              className={cn(
                styles.typeOption,
                type === option.value && styles.typeOptionActive,
                option.comingSoon && styles.typeOptionComingSoon
              )}
              // Listed but not selectable: the report cannot be produced for
              // these yet, so choosing one would only lead somewhere empty.
              disabled={option.comingSoon}
              key={option.value}
              onClick={() => setType(option.value)}
              type="button"
            >
              <span className={styles.typeIcon}>
                <Icon className={styles.typeIconArt} />
              </span>

              <span className={styles.typeText}>
                <span className={styles.typeTitleRow}>
                  <span className={styles.typeTitle}>{option.title}</span>

                  {option.comingSoon && (
                    <span className={styles.comingSoon}>{COPY.comingSoon}</span>
                  )}
                </span>
                {option.qualifier && (
                  <span className={styles.typeQualifier}>
                    {option.qualifier}
                  </span>
                )}
                <span className={styles.typeDescription}>
                  {option.description}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.backButton}
          onClick={() => setStep(1)}
          type="button"
        >
          Back
        </button>

        <button
          className={styles.primaryButton}
          onClick={() => setStep(3)}
          type="button"
        >
          Next
        </button>
      </div>
    </>
  )

  const locationsStep = (
    <>
      {paneHeader(COPY.locationsTitle, COPY.locationsSubtitle)}

      <div className={styles.pickerBody}>
        <div>
          <div className={styles.pickerHeading}>
            <span className={styles.pickerTitle}>{COPY.facilitiesTitle}</span>
            <span className={styles.minPill}>{COPY.min}</span>
          </div>
          <p className={styles.pickerHint}>{COPY.facilitiesHint}</p>

          <div className={styles.facilities}>
            {facilities.map((facility) => {
              const isSelected = selected.includes(facility)
              // At the cap the rest go dead rather than quietly swallowing a
              // click: the report compares one facility against one other, and
              // a chip that looks pickable but is not is the same silence that
              // made Social Listening drop the campaigns it could not draw.
              const isBlocked = !isSelected && selected.length >= MAX_FACILITIES

              return (
                <button
                  className={cn(
                    styles.facility,
                    isSelected && styles.facilityActive,
                    isBlocked && styles.facilityBlocked
                  )}
                  disabled={isBlocked}
                  key={facility}
                  onClick={() => toggleFacility(facility)}
                  type="button"
                >
                  {facility}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          {/* A row of its own, as the facilities heading is: a bare inline span
              let the chip below ride up beside it. */}
          <div className={styles.pickerHeading}>
            <span className={styles.platformsTitle}>{COPY.platformsTitle}</span>
          </div>

          {/* This type aggregates every platform, so the chip states what will
              happen rather than offering a choice that would be ignored. */}
          <div className={styles.platformsNotice}>
            <FontAwesomeIcon
              className={styles.platformsNoticeIcon}
              icon={faExclamationTriangle}
            />
            {COPY.platformsAll}
          </div>

          <p className={styles.platformsNote}>{COPY.platformsNote}</p>
        </div>
      </div>

      <div className={styles.footer}>
        <button
          className={styles.backButton}
          onClick={() => setStep(2)}
          type="button"
        >
          Back
        </button>

        <button
          className={styles.primaryButton}
          disabled={selected.length < MIN_FACILITIES}
          onClick={() =>
            onGenerate(
              selected,
              {
                from: start.format(API_DATE_FORMAT),
                to: end.format(API_DATE_FORMAT),
              },
              activePeriod === CUSTOM_RANGE ? null : activePeriod
            )
          }
          type="button"
        >
          {COPY.generate}
        </button>
      </div>
    </>
  )

  // Portalled to the body: the app zooms .mainContainer to 0.9, so a fixed
  // overlay rendered inside it does not cover the viewport.
  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <aside className={styles.rail}>
          {/* One block: the rail's 64px gap belongs between this and the steps,
              not between the title and its own caption, which is 4px. */}
          <div>
            <h2 className={styles.railTitle}>{COPY.title}</h2>
            <p className={styles.railSteps}>{COPY.steps}</p>
          </div>

          <ol className={styles.stepList}>
            {[
              {
                subtitle: COPY.periodStepSubtitle,
                title: COPY.periodStepTitle,
              },
              { subtitle: COPY.typeStepSubtitle, title: COPY.typeStepTitle },
              {
                subtitle: COPY.locationsStepSubtitle,
                title: COPY.locationsStepTitle,
              },
            ].map((entry, index) => (
              <li
                className={cn(
                  styles.step,
                  step === index + 1 && styles.stepActive
                )}
                key={entry.title}
              >
                <span className={styles.stepNumber}>{index + 1}</span>

                <span className={styles.stepText}>
                  <span className={styles.stepTitle}>{entry.title}</span>
                  {step === index + 1 && (
                    <span className={styles.stepSubtitle}>
                      {entry.subtitle}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </aside>

        <section className={styles.pane}>
          {step === 1 && periodStep}
          {step === 2 && typeStep}
          {step === 3 && locationsStep}
        </section>
      </div>
    </div>,
    document.body
  )
}
